import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchUserProfile } from '../../store/userSlice';
import { 
  Bot, 
  Globe, 
  Cpu, 
  RefreshCw, 
  Loader2, 
  AlertCircle,
  Activity,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Calendar,
  Crown,
  X
} from 'lucide-react';
import {
  getSoraCarList,
  getGptCarList,
  getCCarList,
  getGrokCarList,
  getGptAPICarList,
  getGeminiCarList
} from '../../api/carApi';
import { getAuthBridgeUrl } from '../../config/aiFleetConfig';
import { fleetFilterTags, getTagColorStyles, defaultFilterConfig } from '../../config/nodeFilterTags';
import fleetCardsConfig from '../../config/fleetCardsConfig.json';

export default function AIFleetGrid({ activeTab, tabs, defaultSelectedFleetId, user, navigate, isLoggedIn, onLoginRequired }) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [forceUpdate, setForceUpdate] = useState(0);
  const [fleets, setFleets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFleet, setSelectedFleet] = useState(null);
  const [nodeData, setNodeData] = useState({}); // 存储节点数据
  const [loadingNodes, setLoadingNodes] = useState(false); // 节点加载状态
  
  // 筛选标签状态
  const [activeFilters, setActiveFilters] = useState(defaultFilterConfig.defaultSelected);
  const [filteredNodes, setFilteredNodes] = useState([]); // 筛选后的节点
  const cleanupTimeoutRef = React.useRef(null); // 添加timeout引用
  const abortControllerRef = React.useRef(null); // 添加取消请求的控制器
  const isMountedRef = React.useRef(true); // 跟踪组件是否已挂载
  
  // Tooltip状态
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    fleetInfo: null
  });
  
  // 过期弹窗状态
  const [expiredModal, setExpiredModal] = useState({
    visible: false,
    fleetName: '',
    expiryDate: null
  });

  // 手动跳转弹窗状态
  const [manualRedirectModal, setManualRedirectModal] = useState({
    visible: false,
    bridgeUrl: '',
    fleetName: ''
  });
  
  // 获取用户信息
  const userInfo = useSelector(state => state.user.user);

  // 监听语言变化，强制重新渲染
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(prev => prev + 1);
    };

    // 监听i18n语言变化事件
    i18n.on('languageChanged', handleLanguageChange);
    
    // 监听自定义语言变化事件
    window.addEventListener('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [i18n]);
  
  // 组件卸载时清理
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // 清理超时定时器
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
      // 取消所有进行中的请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // 页面刷新时自动刷新用户信息
  useEffect(() => {
    const refreshUserInfo = async () => {
      // 只有在用户已登录时才刷新用户信息
      if (isLoggedIn) {
        try {
          console.log('🔄 AI Fleet页面刷新用户信息...');
          await dispatch(fetchUserProfile()).unwrap();
          console.log('✅ 用户信息刷新成功');
        } catch (error) {
          console.error('❌ 刷新用户信息失败:', error);
          // 刷新失败不影响页面正常使用，只是用户信息可能不是最新的
        }
      }
    };

    refreshUserInfo();
  }, []); // 只在组件挂载时执行一次

  // 根据AI类型映射图标、颜色、图片和背景色
  const getAIStyle = (type) => {
    const styles = {
      'claude': {
        icon: Cpu,
        gradient: 'from-purple-500 to-purple-600',
        name: t('aiFleet.fleetNames.claudePro'),
        image: '/images/claude-main.jpg',
        bgColor: 'bg-purple-50',     // 紫色背景
        borderColor: 'border-purple-200'
      },
      'gemini': {
        icon: Globe,
        gradient: 'from-green-500 to-green-600',
        name: t('aiFleet.fleetNames.geminiPro'),
        image: '/images/gemini-main.jpg',
        bgColor: 'bg-green-50',      // 绿色背景
        borderColor: 'border-green-200'
      },
      'sora': {
        icon: Globe,
        gradient: 'from-blue-500 to-blue-600',
        name: t('aiFleet.fleetNames.sora'),
        image: '/images/sora-main.jpg',
        bgColor: 'bg-blue-50',       // 蓝色背景
        borderColor: 'border-blue-200'
      },
      'grok': {
        icon: Activity,
        gradient: 'from-orange-500 to-orange-600',
        name: t('aiFleet.fleetNames.grokSuper'),
        image: '/images/grok-main.jpg',
        bgColor: 'bg-orange-50',     // 橙色背景（主色调）
        borderColor: 'border-orange-200'
      },
      'gpt-backup': {
        icon: Bot,
        gradient: 'from-gray-500 to-gray-600',
        name: t('aiFleet.fleetNames.gptPlus'),
        image: '/images/gpt-main.jpg',
        bgColor: 'bg-gray-50',       // 灰色背景
        borderColor: 'border-gray-200'
      },
      'default': {
        icon: Bot,
        gradient: 'from-gray-500 to-gray-600',
        name: t('aiFleet.fleetNames.aiFleet'),
        image: '/images/gpt-main.jpg',
        bgColor: 'bg-gray-50',       // 灰色背景
        borderColor: 'border-gray-200'
      }
    };
    return styles[type] || styles.default;
  };

  // 转换Sora API数据为统一格式
  const convertSoraData = (soraData) => {
    if (!soraData || !soraData.list) return [];
    
    // 先转换数据
    const convertedData = soraData.list.map((node, index) => {
      // 根据颜色映射状态文字
      let statusText = '';
      if (node.color === 'green') {
        statusText = t('aiFleet.busyLevel.idle');
      } else if (node.color === 'yellow') {
        statusText = t('aiFleet.busyLevel.moderate');
      } else if (node.color === 'red' || node.status !== 1) {
        statusText = t('aiFleet.status.stopped');
      } else {
        statusText = t('aiFleet.busyLevel.idle'); // 默认为空闲
      }
      
      return {
        id: node.carID,
        name: `${t('aiFleet.nodeInfo.nodePrefix')} ${index + 1}`,
        status: node.color === 'red' ? 'maintenance' : (node.status === 1 ? 'online' : 'offline'),
        busyLevel: Math.round(Math.min(((node.count || 0) / 20) * 100, 100)), // Sora: count÷20
        description: statusText,
        features: [
          node.isPlus ? `${t('aiFleet.accountTypes.plus')}${t('common.version')}` : '',
          node.isPro ? `${t('aiFleet.accountTypes.pro')}${t('common.version')}` : '',
          `${node.count || 0}${t('aiFleet.nodeInfo.peopleUsing')}`
        ].filter(Boolean),
        region: t('aiFleet.regions.global'),
        speed: node.color === 'green' ? t('aiFleet.responses.ultraFast') : node.color === 'yellow' ? t('aiFleet.responses.normal') : t('aiFleet.responses.slow'),
        price: node.isPlus ? `¥300${t('aiFleet.pricing.perMonth')}` : `¥150${t('aiFleet.pricing.perMonth')}`,
        isPlus: node.isPlus,
        isPro: node.isPro,
        color: node.color || 'green',
        count: node.count || 0  // 保留原始count用于排序
      };
    });
    
    // 排序：先按Plus/普通分组，再按使用率排序
    return convertedData.sort((a, b) => {
      // 首先，Plus账号排在前面
      if ((a.isPlus || a.isPro) && !(b.isPlus || b.isPro)) return -1;
      if (!(a.isPlus || a.isPro) && (b.isPlus || b.isPro)) return 1;
      
      // 同组内，按使用人数从少到多排序
      return a.count - b.count;
    });
  };

  // 转换Grok API数据为统一格式
  const convertGrokData = (grokData) => {
    if (!grokData || !grokData.list) return [];
    
    // 先转换数据
    const convertedData = grokData.list.map((node, index) => {
      // 根据颜色映射状态文字
      let statusText = '';
      if (node.color === 'green') {
        statusText = t('aiFleet.busyLevel.idle');
      } else if (node.color === 'yellow') {
        statusText = t('aiFleet.busyLevel.moderate');
      } else if (node.color === 'red' || node.status !== 1) {
        statusText = t('aiFleet.status.stopped');
      } else {
        statusText = t('aiFleet.busyLevel.idle'); // 默认为空闲
      }
      
      return {
        id: node.carID,
        name: `${t('aiFleet.nodeInfo.nodePrefix')} ${index + 1}`,
        status: node.color === 'red' ? 'maintenance' : (node.status === 1 ? 'online' : 'offline'),
        busyLevel: Math.round(Math.min(((node.count || 0) / 50) * 100, 100)), // Grok: count÷50
        description: statusText,
        features: [
          node.isSuper ? t('aiFleet.accountTypes.superAccount') : t('aiFleet.accountTypes.freeAccount'),
          `${node.count || 0}${t('aiFleet.nodeInfo.peopleUsing')}`
        ].filter(Boolean),
        region: t('aiFleet.regions.global'),
        speed: node.color === 'green' ? t('aiFleet.responses.ultraFast') : node.color === 'yellow' ? t('aiFleet.responses.normal') : t('aiFleet.responses.slow'),
        price: node.isSuper ? `¥299${t('aiFleet.pricing.perMonth')}` : t('aiFleet.pricing.free'),
        isSuper: node.isSuper,
        label: node.label,
        color: node.color,
        count: node.count || 0  // 保留原始count用于排序
      };
    });
    
    // 排序：先按Super/Free分组，再按使用率排序
    return convertedData.sort((a, b) => {
      // 首先，Super账号排在前面
      if (a.isSuper && !b.isSuper) return -1;
      if (!a.isSuper && b.isSuper) return 1;
      
      // 同组内，按使用人数从少到多排序
      return a.count - b.count;
    });
  };

  // 转换Claude API数据为统一格式
  const convertClaudeData = (claudeData) => {
    if (!claudeData || !claudeData.list) return [];
    
    // 先转换数据
    const convertedData = claudeData.list.map((node, index) => {
      // 根据API字段判断账号类型
      let accountType = '';
      let accountLabel = '';
      if (node.isMax) {
        accountType = 'max';
        accountLabel = 'MAX';
      } else if (node.isPro) {
        accountType = 'pro';
        accountLabel = 'PRO';
      } else {
        accountType = 'free';
        accountLabel = 'FREE';
      }

      // 简化状态逻辑：只要不是red就按green处理
      let statusText = '';
      let normalizedColor = '';
      if (node.color === 'red' || node.status !== 1) {
        statusText = t('aiFleet.status.stopped');
        normalizedColor = 'red';
      } else {
        // 非red状态统一按green处理
        statusText = t('aiFleet.busyLevel.idle');
        normalizedColor = 'green';
      }

      // MAX账号使用不同的分母计算繁忙度
      let busyLevel;
      if (accountType === 'max') {
        // MAX账号: count÷200
        busyLevel = Math.round(Math.min(((node.count || 0) / 500) * 100, 100));
      } else {
        // 其他账号: count÷40
        busyLevel = Math.round(Math.min(((node.count || 0) / 40) * 100, 100));
      }

      return {
        id: node.carID,
        name: `${t('aiFleet.nodeInfo.nodePrefix')} ${index + 1}`,
        status: normalizedColor === 'red' ? 'maintenance' : (node.status === 1 ? 'online' : 'offline'),
        busyLevel: busyLevel,
        description: statusText,
        features: [
          accountType === 'max' ? 'Max账号' :
          accountType === 'pro' ? 'Pro账号' : '免费账号',
          `${node.count || 0}${t('aiFleet.nodeInfo.peopleUsing')}`
        ].filter(Boolean),
        region: t('aiFleet.regions.global'),
        speed: normalizedColor === 'green' ? t('aiFleet.responses.ultraFast') : t('aiFleet.responses.slow'),
        price: accountType === 'max' ? '¥300/月' :
               accountType === 'pro' ? '¥200/月' : '免费',

        // Claude车队专用字段
        isMax: node.isMax,
        isPro: node.isPro,
        accountType: accountType,
        label: accountLabel,
        color: normalizedColor, // 使用标准化后的颜色
        count: node.count || 0,  // 保留原始count用于排序
        clears_in: node.clears_in || 0 // 停运恢复时间
      };
    });
    
    // 排序：先按Max/Pro/Free分组，再按使用率排序
    return convertedData.sort((a, b) => {
      // 首先，Max账号排在最前面
      if (a.accountType === 'max' && b.accountType !== 'max') return -1;
      if (a.accountType !== 'max' && b.accountType === 'max') return 1;
      
      // 然后，Pro账号排在Free前面
      if (a.accountType === 'pro' && b.accountType === 'free') return -1;
      if (a.accountType === 'free' && b.accountType === 'pro') return 1;
      
      // 同组内，按使用人数从少到多排序
      return a.count - b.count;
    });
  };

  // 转换Gemini API数据为统一格式
  const convertGeminiData = (geminiData) => {
    if (!geminiData || !geminiData.list) return [];

    // 先转换数据
    const convertedData = geminiData.list.map((node, index) => {
      // 根据颜色映射状态文字
      let statusText = '';
      if (node.color === 'green') {
        statusText = t('aiFleet.busyLevel.idle');
      } else if (node.color === 'yellow') {
        statusText = t('aiFleet.busyLevel.moderate');
      } else if (node.color === 'red' || node.status !== 1) {
        statusText = t('aiFleet.status.stopped');
      } else {
        statusText = t('aiFleet.busyLevel.idle'); // 默认为空闲
      }

      // 根据API的label字段判断账号类型
      const isPro = node.label === 'PRO' || node.isPro;
      const isUltra = node.isUltra || false;

      return {
        id: node.carID,
        name: `${t('aiFleet.nodeInfo.nodePrefix')} ${index + 1}`,
        status: node.color === 'red' ? 'maintenance' : (node.status === 1 ? 'online' : 'offline'),
        busyLevel: Math.round(Math.min(((node.count || 0) / 50) * 100, 100)), // Gemini: count÷50
        description: statusText,
        features: [
          isPro ? `${t('aiFleet.accountTypes.pro')}${t('common.version')}` :
          isUltra ? 'Ultra版' :
          t('aiFleet.accountTypes.freeAccount'),
          `${node.count || 0}${t('aiFleet.nodeInfo.peopleUsing')}`
        ].filter(Boolean),
        region: t('aiFleet.regions.global'),
        speed: node.color === 'green' ? t('aiFleet.responses.ultraFast') : node.color === 'yellow' ? t('aiFleet.responses.normal') : t('aiFleet.responses.slow'),
        price: isPro ? `¥199${t('aiFleet.pricing.perMonth')}` : isUltra ? `¥299${t('aiFleet.pricing.perMonth')}` : t('aiFleet.pricing.free'),
        isPro: isPro,
        isUltra: isUltra,
        label: node.label || 'free', // 保存原始label字段
        color: node.color || 'green', // 保存原始color字段
        count: node.count || 0  // 保留原始count用于排序
      };
    });

    // 排序：按count从小到大排序
    return convertedData.sort((a, b) => {
      return a.count - b.count;
    });
  };

  // 转换GPT Plus车队API数据为统一格式
  const convertGptBackupData = (gptData) => {
    if (!gptData || !gptData.list) return [];
    
    // 先转换数据
    const convertedData = gptData.list.map((node, index) => {
      // 根据颜色映射状态文字
      let statusText = '';
      if (node.color === 'green') {
        statusText = t('aiFleet.busyLevel.idle');
      } else if (node.color === 'yellow') {
        statusText = t('aiFleet.busyLevel.moderate');
      } else if (node.color === 'red' || node.status !== 1) {
        statusText = t('aiFleet.status.stopped');
      } else {
        statusText = t('aiFleet.busyLevel.idle'); // 默认为空闲
      }

      // 使用真实API数据
      // iqStatus: "1" = 满血，其他值 = 降智
      const isFullPower = node.iqStatus === "1";
      
      // renewal_status: 1 = 月续，0 = 不续费
      const autoRenewal = node.renewal_status === 1;
      
      // 从API获取真实到期时间
      const expirationDate = node.expiresAt;
      
      // 账号类型从API的label字段获取
      const accountType = node.label ? node.label.toLowerCase() : 'free'; // TEAM -> team, PLUS -> plus
      const isTeam = node.label === 'TEAM';
      
      return {
        id: node.carID,
        name: `${t('aiFleet.nodeInfo.nodePrefix')} ${index + 1}`,
        status: node.color === 'red' ? 'maintenance' : (node.status === 1 ? 'online' : 'offline'),
        busyLevel: Math.round(Math.min(((node.count || 0) / 100) * 100, 100)), // GPT备用: count÷100
        description: statusText,
        features: [
          node.isSuper ? t('aiFleet.accountTypes.plusAccount') : t('aiFleet.accountTypes.regularAccount'),
          `${node.count || 0}${t('aiFleet.nodeInfo.peopleUsing')}`
        ].filter(Boolean),
        region: t('aiFleet.regions.global'),
        speed: node.color === 'green' ? t('aiFleet.responses.ultraFast') : node.color === 'yellow' ? t('aiFleet.responses.normal') : t('aiFleet.responses.slow'),
        price: node.isSuper ? `¥120${t('aiFleet.pricing.perMonth')}` : `¥60${t('aiFleet.pricing.perMonth')}`,
        isSuper: node.isSuper,
        label: node.label,
        color: node.color,
        count: node.count || 0,  // 保留原始count用于排序

        // GPT车队专用的标签数据 - 使用真实API数据
        accountType: accountType,
        isTeam: isTeam,
        intelligenceLevel: isFullPower ? 'full' : 'degraded',
        isFullPower: isFullPower, // iqStatus === "1" 为满血，否则为降智
        renewalType: autoRenewal ? 'monthly' : 'expire',
        autoRenewal: autoRenewal, // renewal_status === 1 为月续，否则为不续费
        expirationDate: expirationDate // 使用API返回的真实到期时间
      };
    });
    
    // 过滤掉FREE账号，只保留TEAM和PLUS
    const filteredData = convertedData.filter(node => 
      node.label === 'TEAM' || node.label === 'PLUS'
    );
    
    // 排序：先按满血/降智状态，再按使用人数排序
    return filteredData.sort((a, b) => {
      // 首先，满血账号排在降智账号前面
      if (a.isFullPower && !b.isFullPower) return -1;
      if (!a.isFullPower && b.isFullPower) return 1;
      
      // 同状态内按使用人数从少到多排序
      return a.count - b.count;
    });
  };

  // 车队数据 - 从配置文件读取 - 简化版本，不支持多语言
  const fleetInfo = React.useMemo(() => {
    return Object.values(fleetCardsConfig.fleetCards).map(fleet => ({
      id: fleet.id,
      name: fleet.name,
      type: fleet.type,
      status: fleet.status
    }));
  }, [forceUpdate]); // 依赖于forceUpdate

  // 获取节点数据
  const fetchNodeData = async (fleet, retryCount = 0) => {
    if (!fleet || !isMountedRef.current) return;
    
    // 清理之前的超时
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
    
    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // 创建新的AbortController
    abortControllerRef.current = new AbortController();
    
    if (isMountedRef.current) {
      setLoadingNodes(true);
      setError(''); // 清除之前的错误
    }
    
    // 设置5秒超时定时器
    const timeoutId = setTimeout(() => {
      if (!isMountedRef.current) return;
      
      console.log(`加载${fleet.name}节点数据超时，准备重试...`);
      
      // 检查是否还需要重试
      if (retryCount < 3) {
        console.log(`第${retryCount + 1}次重试加载${fleet.name}节点数据`);
        fetchNodeData(fleet, retryCount + 1);
      } else {
        console.error(`加载${fleet.name}节点数据失败，已重试${retryCount}次`);
        if (isMountedRef.current) {
          setLoadingNodes(false);
          setError(`加载${fleet.name}节点数据超时，请稍后重试`);
          setNodeData(prev => ({
            ...prev,
            [fleet.id]: []
          }));
        }
      }
    }, 5000); // 5秒超时
    
    cleanupTimeoutRef.current = timeoutId;
    
    try {
      let data = [];
      
      switch(fleet.type) {
        case 'sora':
          const soraResponse = await getSoraCarList({}, { signal: abortControllerRef.current?.signal });
          if (soraResponse && soraResponse.code === 1 && soraResponse.data) {
            data = convertSoraData(soraResponse.data);
          }
          break;
          
        case 'claude':
          const claudeResponse = await getCCarList({}, { signal: abortControllerRef.current?.signal });
          if (claudeResponse && claudeResponse.code === 1 && claudeResponse.data) {
            data = convertClaudeData(claudeResponse.data);
          }
          break;

        case 'gemini':
          const geminiResponse = await getGeminiCarList({}, { signal: abortControllerRef.current?.signal });
          if (geminiResponse && geminiResponse.code === 1 && geminiResponse.data) {
            data = convertGeminiData(geminiResponse.data);
          }
          break;

        case 'grok':
          const grokResponse = await getGrokCarList({}, { signal: abortControllerRef.current?.signal });
          if (grokResponse && grokResponse.code === 1 && grokResponse.data) {
            data = convertGrokData(grokResponse.data);
          }
          break;
          
        case 'gpt-backup':
          // GPT Plus车队使用getGptCarList API
          const gptBackupResponse = await getGptCarList({}, { signal: abortControllerRef.current?.signal });
          if (gptBackupResponse && gptBackupResponse.code === 1 && gptBackupResponse.data) {
            data = convertGptBackupData(gptBackupResponse.data);
          }
          break;
          
        default:
          data = [];
      }
      
      // 清除超时定时器
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
      
      // 只在组件仍然挂载时更新状态
      if (isMountedRef.current) {
        setNodeData(prev => ({
          ...prev,
          [fleet.id]: data
        }));
        setLoadingNodes(false);
        setError(''); // 清除错误信息
      }
    } catch (error) {
      // 清除超时定时器
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
      
      // 如果是取消请求的错误，忽略
      if (error.name === 'AbortError' || !isMountedRef.current) {
        return;
      }
      
      console.error('获取节点数据失败:', error);
      
      // 如果是网络错误且重试次数小于3次，自动重试
      if (retryCount < 3 && isMountedRef.current) {
        console.log(`获取${fleet.name}节点数据出错，第${retryCount + 1}次重试...`);
        setTimeout(() => {
          if (isMountedRef.current) {
            fetchNodeData(fleet, retryCount + 1);
          }
        }, 1000); // 1秒后重试
      } else if (isMountedRef.current) {
        setNodeData(prev => ({
          ...prev,
          [fleet.id]: []
        }));
        setLoadingNodes(false);
        setError(`${t('aiFleet.error')} ${fleet.name}`);
      }
    }
  };

  // 组件初始化时设置车队列表
  useEffect(() => {
    setFleets(fleetInfo);
    setLoading(false);
  }, []);

  // 初始化时选中第一个车队或默认指定的车队
  useEffect(() => {
    if (fleets.length > 0 && !selectedFleet) {
      const defaultFleet = fleets.find(f => f.type === activeTab) || fleets[0];
      setSelectedFleet(defaultFleet);
    }
  }, [fleets, activeTab, selectedFleet]);

  // 当选中车队变化时，获取节点数据
  useEffect(() => {
    if (selectedFleet && isMountedRef.current) {
      // 取消之前的所有请求和定时器
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      fetchNodeData(selectedFleet, 0);
    }
  }, [selectedFleet]);

  // 当节点数据或筛选条件变化时，更新筛选结果
  useEffect(() => {
    if (selectedFleet && nodeData[selectedFleet.id]) {
      console.log('开始筛选节点:', {
        fleet: selectedFleet.type,
        nodeCount: nodeData[selectedFleet.id].length,
        activeFilters: activeFilters,
        hasActiveFilters: Object.values(activeFilters).filter(Boolean).length > 0
      });
      
      const filtered = filterNodes(nodeData[selectedFleet.id], activeFilters, selectedFleet.type);
      console.log('筛选后节点数量:', filtered.length);
      setFilteredNodes(filtered);
    } else {
      console.log('没有车队数据或选中车队:', {
        selectedFleet: selectedFleet?.type,
        hasNodeData: !!nodeData[selectedFleet?.id]
      });
      setFilteredNodes([]);
    }
  }, [nodeData, activeFilters, selectedFleet]);

  const handleFleetClick = (fleet) => {
    try {
      console.log('选中车队:', fleet);
      
      // 取消之前的所有请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // 切换车队时重置筛选状态 - 确保显示所有节点
      setActiveFilters({});
      
      // 直接选中车队，不检查过期
      setSelectedFleet(fleet);
      
      // 更新路由以反映当前选中的车队
      navigate(`/ai-fleet/${fleet.type}`, { replace: true });
    } catch (error) {
      console.error('切换车队时出错:', error);
      if (isMountedRef.current) {
        setError('切换车队失败，请重试');
      }
    }
  };



  // 处理鼠标进入卡片
  const handleMouseEnter = (e, fleet) => {
    // 初始位置设置在鼠标右下方，与handleMouseMove保持一致
    setTooltip({
      visible: true,
      x: e.clientX + 15,  // 鼠标右边15px
      y: e.clientY + 15,  // 鼠标下方15px
      fleetInfo: fleet
    });
  };

  // 处理鼠标移动
  const handleMouseMove = (e) => {
    // 检查鼠标是否在按钮区域
    const target = e.target;
    const isOverButton = target.tagName === 'BUTTON' || target.closest('button');
    
    // 检查鼠标是否还在卡片内
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const isInsideCard = e.clientX >= rect.left && 
                         e.clientX <= rect.right && 
                         e.clientY >= rect.top && 
                         e.clientY <= rect.bottom;
    
    if (!isInsideCard) {
      // 如果鼠标不在卡片内，立即隐藏tooltip
      setTooltip(prev => ({ ...prev, visible: false }));
      return;
    }
    
    if (isOverButton) {
      // 如果在按钮上，隐藏tooltip
      setTooltip(prev => ({ ...prev, visible: false }));
    } else if (tooltip.fleetInfo) {
      // 否则更新tooltip位置 - 始终保持在鼠标右下方
      setTooltip(prev => ({
        ...prev,
        visible: true,
        x: e.clientX + 15,  // 向右偏移
        y: e.clientY + 15   // 向下偏移
      }));
    }
  };

  // 处理鼠标离开
  const handleMouseLeave = () => {
    setTooltip({
      visible: false,
      x: 0,
      y: 0,
      fleetInfo: null
    });
  };

  // 获取车队的提示信息 - 从配置文件读取 - 简化版本，不支持多语言
  const getFleetTooltipInfo = React.useMemo(() => {
    return (fleetType) => {
      const fleet = fleetCardsConfig.fleetCards[fleetType];
      if (!fleet || !fleet.tooltip) return {
        features: ['暂无信息'],
        price: '暂无信息',
        limits: ['暂无信息']
      };
      
      return fleet.tooltip;
    };
  }, [forceUpdate]);

  const handleRetry = () => {
    setError(''); // 清除错误信息
    if (selectedFleet) {
      fetchNodeData(selectedFleet, 0); // 重置重试次数
    }
  };

  // 筛选节点逻辑 - 基于当前车队类型
  const filterNodes = (nodes, filters, fleetType) => {
    if (!nodes || nodes.length === 0) return [];
    
    // 如果没有任何激活的筛选条件，返回所有节点
    const activeFilterValues = Object.values(filters).filter(Boolean);
    if (activeFilterValues.length === 0) {
      console.log('没有激活的筛选条件，返回所有节点:', nodes.length, '个节点');
      return nodes;
    }
    
    // 获取当前车队的筛选配置
    const fleetTags = fleetFilterTags[fleetType];
    if (!fleetTags) return nodes;
    
    return nodes.filter(node => {
      // 检查每个激活的筛选器
      return Object.entries(filters).every(([category, selectedTagId]) => {
        if (!selectedTagId || !fleetTags[category]) return true;
        
        const selectedTag = fleetTags[category].find(tag => tag.id === selectedTagId);
        if (!selectedTag) return true;
        
        return selectedTag.filter(node);
      });
    });
  };

  // 处理筛选标签点击
  const handleFilterClick = (category, tagId) => {
    setActiveFilters(prev => {
      // 如果点击的是当前选中的标签，取消选择
      if (prev[category] === tagId) {
        const newFilters = { ...prev };
        delete newFilters[category];
        return newFilters;
      }
      
      // 否则选择新标签
      return {
        ...prev,
        [category]: tagId
      };
    });
  };

  // 获取状态颜色和图标
  const getStatusInfo = (status) => {
    switch(status) {
      case 'online':
        return {
          color: 'text-green-600 bg-green-100',
          text: t('aiFleet.status.running'),
          icon: CheckCircle
        };
      case 'testing':
        return {
          color: 'text-blue-600 bg-blue-100',
          text: '内部测试',
          icon: Activity
        };
      case 'maintenance':
        return {
          color: 'text-red-600 bg-red-100',
          text: t('aiFleet.status.stopped'),
          icon: XCircle
        };
      case 'limited':
        return {
          color: 'text-yellow-600 bg-yellow-100',
          text: t('aiFleet.status.limitedRunning'),
          icon: AlertCircle
        };
      default:
        return {
          color: 'text-gray-600 bg-gray-100',
          text: t('aiFleet.status.offline'),
          icon: XCircle
        };
    }
  };

  // 根据繁忙程度获取进度条颜色
  const getBusyLevelStyle = (busyLevel, node) => {
    // 对于Grok，使用特殊的映射逻辑
    if (node && node.color) {
      if (node.color === 'green') {
        return {
          color: 'bg-green-500',
          barColor: 'bg-green-500',
          borderColor: 'border-green-200',
          text: '空闲推荐',
          label: '空闲',
          textColor: 'text-green-600'
        };
      } else if (node.color === 'yellow') {
        return {
          color: 'bg-yellow-500',
          barColor: 'bg-yellow-500',
          borderColor: 'border-yellow-200',
          text: '适中负载',
          label: '适中',
          textColor: 'text-yellow-600'
        };
      } else if (node.color === 'red') {
        return {
          color: 'bg-red-500',
          barColor: 'bg-red-500',
          borderColor: 'border-red-200',
          text: '停运',
          label: '停运',
          textColor: 'text-red-600'
        };
      }
    }
    
    // 默认根据使用率判断
    if (busyLevel <= 30) {
      return {
        color: 'bg-green-500',
        barColor: 'bg-green-500',
        borderColor: 'border-green-200',
        text: t('aiFleet.busyLevel.idleRecommended'),
        label: t('aiFleet.busyLevel.idle'),
        textColor: 'text-green-600'
      };
    } else if (busyLevel <= 70) {
      return {
        color: 'bg-yellow-500',
        barColor: 'bg-yellow-500',
        borderColor: 'border-yellow-200',
        text: t('aiFleet.busyLevel.moderateLoad'),
        label: t('aiFleet.busyLevel.moderate'),
        textColor: 'text-yellow-600'
      };
    } else {
      return {
        color: 'bg-red-500',
        barColor: 'bg-red-500',
        borderColor: 'border-red-200',
        text: t('aiFleet.busyLevel.busyState'),
        label: t('aiFleet.busyLevel.busy'),
        textColor: 'text-red-600'
      };
    }
  };

  // Loading组件
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-24">
      <div className="flex flex-col items-center gap-6">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-gray-200 rounded-full"></div>
          <div className="w-20 h-20 border-4 border-orange-500 rounded-full border-t-transparent animate-spin absolute inset-0"></div>
        </div>
        <div className="text-center">
          <p className="text-lg font-medium text-gray-700">{t('aiFleet.loading')}</p>
          <p className="text-sm text-gray-500 mt-1">{t('common.loading')}</p>
        </div>
      </div>
    </div>
  );

  // 错误提示组件
  const ErrorMessage = () => (
    <div className="flex items-center justify-center py-12">
      <div className="text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('aiFleet.loadFailed')}</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={handleRetry}
          className="inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors duration-300"
        >
          <RefreshCw className="w-4 h-4" />
          {t('aiFleet.retry')}
        </button>
      </div>
    </div>
  );

  // 空状态组件
  const EmptyState = () => (
    <div className="flex items-center justify-center py-12">
      <div className="text-center">
        <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
          <Bot className="w-12 h-12 text-gray-400" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">{t('aiFleet.noFleets')}</h3>
        <p className="text-gray-600 mb-4">{t('aiFleet.noFleetsDesc')}</p>
        <button
          onClick={handleRetry}
          className="inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors duration-300"
        >
          <RefreshCw className="w-4 h-4" />
          {t('aiFleet.refresh')}
        </button>
      </div>
    </div>
  );

  // 节点网格组件
  const NodeGrid = ({ nodes, fleetType }) => {
    if (!nodes || nodes.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500">{t('aiFleet.nodeInfo.noAvailableNodes')}</p>
        </div>
      );
    }

    const aiStyle = getAIStyle(fleetType);

    return (
      <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 mt-4 sm:mt-6">
        {nodes.map((node, index) => {
          const statusInfo = getStatusInfo(node.status);
          const busyStyle = getBusyLevelStyle(node.busyLevel, node);
          
          return (
            <div
              key={node.id}
              className={`bg-white rounded-lg sm:rounded-xl border hover:shadow-lg transition-all duration-300 overflow-hidden group ${
                fleetType === 'claude' ? 'border-purple-200 hover:border-purple-400' :
                fleetType === 'gemini' ? 'border-green-200 hover:border-green-400' :
                fleetType === 'sora' ? 'border-blue-200 hover:border-blue-400' :
                fleetType === 'grok' ? 'border-orange-200 hover:border-orange-400' :
                fleetType === 'gpt-backup' ? 'border-gray-200 hover:border-gray-400' :
                'border-gray-200 hover:border-gray-400'
              } ${fleetType === 'gpt-backup' ? 'min-h-[280px]' : ''}`} // GPT卡片稍微调低高度适应5列布局
            >
              {/* 顶部彩色条 - 只有停运是红色，其他都是绿色 */}
              <div className={`h-1.5 ${
                node.status === 'maintenance' ? 'bg-red-500' : 'bg-green-500'
              }`}></div>
              
              {/* 顶部图片区域 - GPT车队稍高 */}
              <div className={`relative ${fleetType === 'gpt-backup' ? 'h-20' : 'h-16'} ${
                fleetType === 'claude' ? 'bg-gradient-to-br from-purple-50 to-purple-100' :
                fleetType === 'gemini' ? 'bg-gradient-to-br from-green-50 to-green-100' :
                fleetType === 'sora' ? 'bg-gradient-to-br from-blue-50 to-blue-100' :
                fleetType === 'grok' ? 'bg-gradient-to-br from-orange-50 to-orange-100' :
                fleetType === 'gpt-backup' ? 'bg-gradient-to-br from-gray-50 to-gray-100' :
                'bg-gradient-to-br from-gray-50 to-gray-100'
              } flex items-center justify-between px-3`}>
                {/* 左侧：Logo - 调大尺寸 */}
                <div className="flex items-center gap-2">
                  <div className={`w-12 h-12 rounded-lg overflow-hidden ${
                    fleetType === 'claude' ? 'bg-purple-100' :
                    fleetType === 'gemini' ? 'bg-green-100' :
                    fleetType === 'sora' ? 'bg-blue-100' :
                    fleetType === 'grok' ? 'bg-orange-100' :
                    fleetType === 'gpt-backup' ? 'bg-gray-100' :
                    'bg-white'
                  } shadow-sm flex items-center justify-center`}>
                    <img 
                      src={aiStyle.image}
                      alt={aiStyle.name}
                      className="w-10 h-10 object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div 
                      className={`w-10 h-10 bg-gradient-to-br ${aiStyle.gradient} rounded flex items-center justify-center`}
                      style={{display: 'none'}}
                    >
                      {React.createElement(aiStyle.icon, { className: "w-5 h-5 text-white" })}
                    </div>
                  </div>
                </div>
                
                {/* 右侧：GPT车队横排标签区域 - 优化为更紧凑布局 */}
                {fleetType === 'gpt-backup' ? (
                  <div className="flex items-center gap-1 flex-wrap justify-end">
                    {/* 账号类型标签 */}
                    <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                      node.label === 'TEAM' ? 'bg-purple-500 text-white' :
                      node.label === 'PLUS' ? 'bg-orange-500 text-white' :
                      'bg-gray-600 text-white'
                    }`}>
                      {node.label || 'FREE'}
                    </span>
                    
                    {/* 降智/满血标签 - 缩小间距和padding */}
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      node.isFullPower ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
                    }`}>
                      {node.isFullPower ? '满血' : '降智'}
                    </span>
                    
                    {/* 状态标签 - 最紧凑 */}
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      node.color === 'green' ? 'bg-green-600 text-white' :
                      node.color === 'yellow' ? 'bg-yellow-600 text-white' :
                      'bg-red-600 text-white'
                    }`}>
                      {node.color === 'green' ? '空闲' :
                       node.color === 'yellow' ? '适中' : '繁忙'}
                    </span>
                  </div>
                ) : (
                  /* 其他车队保持原有的单标签 */
                  (node.isSuper !== undefined || node.isPlus !== undefined || node.isPro !== undefined || node.isMax !== undefined || node.isUltra !== undefined) && (
                    <div className={`px-3 py-1 rounded-md text-xs font-bold ${
                      // Claude车队的标签颜色
                      fleetType === 'claude' ? (
                        node.isMax ? 'bg-purple-500 text-white' :
                        node.isPro ? 'bg-orange-500 text-white' :
                        'bg-green-500 text-white'
                      ) :
                      // Gemini车队的标签颜色 - 根据label字段
                      fleetType === 'gemini' ? (
                        node.label === 'PRO' ? 'bg-purple-500 text-white' :
                        node.label === 'ULTRA' ? 'bg-orange-500 text-white' :
                        'bg-green-500 text-white'
                      ) : (
                        // 其他车队的标签颜色
                        node.isSuper || node.isPlus || node.isPro
                          ? 'bg-orange-500 text-white'
                          : 'bg-purple-600 text-white'
                      )
                    }`}>
                      {fleetType === 'claude' ? (
                        node.isMax ? 'MAX' :
                        node.isPro ? 'PRO' : 'FREE'
                      ) :
                      fleetType === 'gemini' ? (
                        node.label || 'FREE'
                      ) : (
                        fleetType === 'sora' && node.isPlus ? 'PLUS' :
                        node.isSuper ? 'SUPER' :
                        'FREE'
                      )}
                    </div>
                  )
                )}
              </div>
              
              <div className={`${fleetType === 'gpt-backup' ? 'p-3' : 'p-2 sm:p-3'}`}>
                {/* 节点ID */}
                <div className="text-sm sm:text-base font-bold text-gray-900 mb-2 text-center">
                  {node.id}
                </div>

                {/* GPT车队：底部信息区域 - 优化为更紧凑布局 */}
                {fleetType === 'gpt-backup' && (
                  <div className="mb-2 space-y-1.5">
                    {/* 到期时间 - 更紧凑设计 */}
                    <div className="flex justify-center">
                      <div className="bg-gray-50 border border-gray-200 rounded px-2 py-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-gray-600" />
                        <span className="text-xs text-gray-700">
                          {new Date(node.expirationDate).toLocaleDateString('zh-CN', {
                            month: '2-digit',
                            day: '2-digit'
                          })}到期
                        </span>
                      </div>
                    </div>

                    {/* 续费机制 - 更紧凑设计 */}
                    <div className="flex justify-center">
                      <span className="bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs text-gray-700 flex items-center gap-1">
                        {node.autoRenewal ? '🔄' : '⏰'}
                        {node.autoRenewal ? '月续' : '不续'}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* 状态描述 - 只对非GPT车队显示，GPT车队已在header显示 */}
                {fleetType !== 'gpt-backup' && (
                  <div className="text-center mb-2">
                    <span className={`inline-flex items-center gap-1.5 text-xs ${
                      node.status === 'maintenance' ? 'text-red-600' : 'text-green-600'
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${
                        node.status === 'maintenance' ? 'bg-red-500' : 'bg-green-500'
                      }`}></span>
                      {node.description || busyStyle.label}
                    </span>
                  </div>
                )}
                
                {/* 使用率进度条 - 简化显示，只有停运是红色，其他都是绿色 */}
                <div className={`mb-3 ${fleetType === 'gpt-backup' ? 'mb-4' : ''}`}>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>{t('aiFleet.busyLevel.usageRate')}</span>
                    <span className={`font-medium ${
                      node.status === 'maintenance' ? 'text-red-600' : 'text-green-600'
                    }`}>{node.busyLevel}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 relative ${
                        node.status === 'maintenance' ? 'bg-red-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${node.busyLevel}%` }}
                    >
                      <div className="absolute inset-0 bg-white/20"></div>
                    </div>
                  </div>
                </div>
                
                {/* 立即使用按钮 - GPT车队使用更大的按钮 */}
                <button
                  onClick={() => handleNodeClick(node, fleetType)}
                  disabled={node.status === 'maintenance'}
                  className={`w-full ${fleetType === 'gpt-backup' ? 'py-2.5' : 'py-1.5 sm:py-2'} rounded-md sm:rounded-lg font-medium text-xs sm:text-sm transition-all duration-300 ${
                    node.status === 'maintenance'
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : fleetType === 'gpt-backup'
                      ? 'border-2 border-orange-300 text-orange-600 bg-white hover:bg-orange-50 hover:border-orange-400 hover:shadow-md hover:text-orange-700 font-bold'
                      : 'border border-orange-300 text-orange-600 bg-white hover:bg-orange-50 hover:border-orange-400 hover:shadow-md hover:text-orange-700'
                  }`}
                >
                  {node.status === 'maintenance' ? t('aiFleet.nodeInfo.stopped') : t('aiFleet.nodeInfo.useNow')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const handleNodeClick = (node, fleetType) => {
    // 检查节点是否可用
    if (node.status === 'maintenance') {
      console.log('节点不可用:', node.id);
      return;
    }
    
    // 如果用户已登录，检查该车队是否过期
    if (isLoggedIn && userInfo) {
      let expiryDate = null;
      switch(fleetType) {
        case 'gpt-backup':
          expiryDate = (userInfo && userInfo.expiration_date);
          break;
        case 'claude':
          expiryDate = (userInfo && userInfo.expiration_date_ye_claude);
          break;
        case 'gemini':
          expiryDate = (userInfo && userInfo.expiration_date_ye_gemini);
          break;
        case 'grok':
          expiryDate = (userInfo && userInfo.expiration_date_ye_grok);
          break;
        case 'sora':
          expiryDate = (userInfo && userInfo.expiration_date_ye_sora);
          break;
        default:
          expiryDate = (userInfo && userInfo.expiration_date);
      }
      
      // 如果有过期时间且已过期，显示弹窗
      if (expiryDate) {
        const expiry = new Date(expiryDate);
        const today = new Date();
        if (expiry < today) {
          const fleetName = fleets.find(f => f.type === fleetType)?.name || '该服务';
          setExpiredModal({
            visible: true,
            fleetName: fleetName,
            expiryDate: expiryDate
          });
          return;
        }
      }
    }
    
    // 打印用户信息和节点ID
    console.log('=== 节点点击事件 ===');
    console.log('用户信息:', userInfo);
    console.log('节点ID:', node.id);
    console.log('节点完整信息:', node);
    console.log('所属车队类型:', fleetType);
    console.log('===================');
    
    // 对所有支持的车队类型执行鉴权流程
    const supportedFleets = ['grok', 'sora', 'claude', 'gemini', 'gpt-backup'];
    
    // 如果是支持的车队类型
    if (supportedFleets.includes(fleetType)) {
      // 检查用户是否登录
      if (!isLoggedIn || !userInfo) {
        // 未登录用户，显示登录弹窗
        if (onLoginRequired) {
          onLoginRequired();
        } else {
          // 如果没有传入onLoginRequired函数，使用原有的提示逻辑
          const confirmLogin = window.confirm('使用AI服务需要先登录账号。\n\n点击"确定"前往登录页面，或点击"取消"继续浏览。');
          if (confirmLogin) {
            navigate('/');
          }
        }
        return;
      }
      
      // 已登录用户，继续原有的鉴权流程
      // 1. 获取三个信息
      const carid = node.id; // 车号ID从节点信息中获取
      const uid = userInfo.uid || userInfo.id; // 用户uid
      const userToken = userInfo.userToken; // 登录密码
      
      console.log(`${fleetType.toUpperCase()}鉴权信息:`, {
        carid,
        uid,
        userToken
      });
      
      // 检查必要信息是否完整
      if (!carid || !userToken) {
        alert('缺少必要的鉴权信息，请重新登录');
        return;
      }
      
      // 2. 构建桥接页面URL（从配置文件获取）
      const bridgeParams = new URLSearchParams({
        carid: carid,
        uid: uid,
        userToken: userToken
      });
      
      try {
        // 从配置获取桥接页面URL
        const bridgeUrl = `${getAuthBridgeUrl(fleetType)}?${bridgeParams.toString()}`;
        
        console.log('跳转到鉴权桥接页面:', bridgeUrl);
        
        // 在新窗口打开桥接页面
        window.open(bridgeUrl, '_blank');
        
        // 显示手动跳转弹窗
        const fleetName = fleets.find(f => f.type === fleetType)?.name || `${fleetType.toUpperCase()} 服务`;
        setManualRedirectModal({
          visible: true,
          bridgeUrl: bridgeUrl,
          fleetName: fleetName
        });
      } catch (error) {
        console.error('获取配置失败:', error);
        alert('配置错误，请联系管理员');
      }
      
      return;
    }
    
    // 其他车队的处理逻辑保持不变
    alert(`已选择 ${node.id}\n节点名称: ${node.name}\n状态: ${getStatusInfo(node.status).text}`);
  };

  return (
    <section className="pt-8 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* 车队网格 */}
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorMessage />
        ) : fleets.length > 0 ? (
          <>
            {/* 车队Tab选择器 - 5个立即使用的卡片 - 始终保持一行 */}
            <div className="grid grid-cols-5 gap-2 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
              {fleets.map((fleet, index) => {
                const aiStyle = getAIStyle(fleet.type);
                const IconComponent = aiStyle.icon;
                const statusInfo = getStatusInfo(fleet.status);
                const StatusIcon = statusInfo.icon;
                const isSelected = (selectedFleet && selectedFleet.id) === fleet.id;
                
                // 为每个车队类型固定配色方案
                const fleetColorMap = {
                  'gpt': {
                    third: 'from-blue-200 to-purple-300',
                    second: 'from-green-200 to-teal-300'
                  },
                  'sora': {
                    third: 'from-pink-200 to-rose-300',
                    second: 'from-yellow-200 to-orange-300'
                  },
                  'claude': {
                    third: 'from-purple-200 to-indigo-300',
                    second: 'from-emerald-200 to-cyan-300'
                  },
                  'gemini': {
                    third: 'from-green-200 to-emerald-300',
                    second: 'from-teal-200 to-cyan-300'
                  },
                  'grok': {
                    third: 'from-red-200 to-pink-300',
                    second: 'from-blue-200 to-indigo-300'
                  },
                  'gpt-backup': {
                    third: 'from-amber-200 to-orange-300',
                    second: 'from-violet-200 to-purple-300'
                  }
                };
                
                const colors = fleetColorMap[fleet.type] || fleetColorMap['gpt'];
                
                return (
                  <div
                    key={fleet.id}
                    className={`bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-md hover:shadow-xl transform hover:scale-105 transition-all duration-300 border-2 group cursor-pointer ${
                      isSelected ? 'border-orange-500 ring-2 ring-orange-200' : 'border-gray-100'
                    }`}
                    onClick={() => handleFleetClick(fleet)}
                    onMouseEnter={(e) => handleMouseEnter(e, fleet)}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                  >
                    {/* 车队图标区域 - 正方形 */}
                    <div className="bg-white aspect-square relative overflow-hidden">
                      <div className="relative h-full flex items-center justify-center p-3 sm:p-4">
                        {/* 堆叠卡片背景装饰 */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          {/* 第三层背景卡片 */}
                          <div className={`absolute w-4/5 h-4/5 bg-gradient-to-br ${colors.third} rounded-xl transform rotate-6 group-hover:rotate-12 transition-transform duration-500 opacity-60`}></div>
                          {/* 第二层背景卡片 */}
                          <div className={`absolute w-4/5 h-4/5 bg-gradient-to-br ${colors.second} rounded-xl transform -rotate-3 group-hover:-rotate-6 transition-transform duration-500 opacity-80`}></div>
                        </div>
                        
                        {/* 主车队图片容器 */}
                        <div className="relative z-10 w-4/5 h-4/5">
                          <div className="w-full h-full rounded-xl overflow-hidden group-hover:scale-105 transition-all duration-500 shadow-xl">
                            <img 
                              src={aiStyle.image}
                              alt={fleet.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                // 如果图片加载失败，显示渐变背景和图标
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            
                            {/* 备用图标显示 */}
                            <div 
                              className={`w-full h-full bg-gradient-to-br ${aiStyle.gradient} rounded-xl flex items-center justify-center`}
                              style={{display: 'none'}}
                            >
                              <IconComponent className="w-12 h-12 text-white" />
                            </div>
                          </div>
                          
                          {/* 图片上的光泽效果 */}
                          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent rounded-xl"></div>
                        </div>
                      </div>
                      
                      {/* 状态标签 */}
                      <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-bold z-20 ${statusInfo.color} flex items-center gap-1`}>
                        <StatusIcon className="w-2.5 h-2.5" />
                        <span className="text-xs">{statusInfo.text}</span>
                      </div>
                    </div>

                    {/* 车队信息 - 极简布局 */}
                    <div className="p-3 sm:p-4">
                      <h3 className={`text-xs sm:text-sm font-bold mb-2 sm:mb-3 group-hover:text-orange-500 transition-colors duration-300 leading-tight text-center ${
                        isSelected ? 'text-orange-600' : 'text-gray-900'
                      }`}>
                        {fleet.name}
                      </h3>
                      

                    </div>
                  </div>
                );
              })}
            </div>

            {/* Tooltip组件 */}
            {tooltip.visible && tooltip.fleetInfo && (
              <div
                className="fixed z-50 pointer-events-none"
                style={{
                  left: `${tooltip.x}px`,
                  top: `${tooltip.y}px`
                }}
              >
                <div className="bg-gray-900 text-white rounded-xl shadow-2xl p-4 min-w-[320px]">
                  
                  {/* Tooltip内容 */}
                  <div className="space-y-3">
                    {/* 标题 */}
                    <div className="flex items-center gap-2 border-b border-gray-700 pb-2">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${getAIStyle(tooltip.fleetInfo.type).gradient} flex items-center justify-center`}>
                        {React.createElement(getAIStyle(tooltip.fleetInfo.type).icon, { className: "w-4 h-4 text-white" })}
                      </div>
                      <h4 className="font-bold text-sm">{tooltip.fleetInfo.name}</h4>
                    </div>
                    
                    {/* 功能特点 */}
                    <div>
                      <p className="text-xs text-gray-400 mb-1">主要功能：</p>
                      <ul className="text-xs space-y-1">
                        {getFleetTooltipInfo(tooltip.fleetInfo.type).features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* 价格信息 */}
                    <div className="pt-2 border-t border-gray-700">
                      <div>
                        <p className="text-xs text-gray-400">价格</p>
                        <p className="text-sm font-semibold text-orange-400">
                          {getFleetTooltipInfo(tooltip.fleetInfo.type).price}
                        </p>
                      </div>
                    </div>
                    
                    {/* 使用限制 - 支持多行显示 */}
                    <div className="bg-gray-800 rounded-lg p-2">
                      <p className="text-xs text-gray-400 mb-2">使用限制：</p>
                      <div className="space-y-1">
                        {getFleetTooltipInfo(tooltip.fleetInfo.type).limits.map((limit, idx) => (
                          <div key={idx} className="flex items-start gap-1">
                            <span className="text-yellow-400 text-xs mt-0.5">•</span>
                            <p className="text-xs text-yellow-400">
                              {limit}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 账户状态栏 - 在车队卡片和节点列表之间 */}
            {isLoggedIn && userInfo && selectedFleet && (
              <div className="bg-gradient-to-r from-orange-50 via-yellow-50 to-orange-50 border border-orange-200 rounded-2xl sm:rounded-3xl mb-6 sm:mb-8 shadow-md hover:shadow-lg transition-all duration-300">
                <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                    {/* 左侧：车队信息 */}
                    <div className="flex items-center gap-3 sm:gap-4">
                      {/* 车队图标 - 正方形 */}
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden shadow-md bg-white p-0.5">
                        <img 
                          src={getAIStyle(selectedFleet.type).image}
                          alt={selectedFleet.name}
                          className="w-full h-full rounded-md object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div 
                          className={`w-full h-full bg-gradient-to-br ${getAIStyle(selectedFleet.type).gradient} rounded-md flex items-center justify-center`}
                          style={{display: 'none'}}
                        >
                          {React.createElement(getAIStyle(selectedFleet.type).icon, { className: "w-6 h-6 text-white" })}
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-base sm:text-lg font-bold text-gray-900">
                          {selectedFleet.name}
                        </h3>
                      </div>
                    </div>

                    {/* 中间：用户信息 - 优化布局 */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 lg:gap-8">
                      {/* 用户名 - 显示uid，省略中间部分 */}
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center shadow-sm">
                          <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Username</p>
                          <p className="text-xs sm:text-sm font-semibold text-gray-900" title={userInfo.uid || userInfo.id || userInfo.email || '用户'}>
                            {(() => {
                              const uid = userInfo.uid || userInfo.id || userInfo.email || '用户';
                              // 如果uid长度超过16个字符，省略中间部分
                              if (uid.length > 16) {
                                const start = uid.substring(0, 6);
                                const end = uid.substring(uid.length - 6);
                                return `${start}...${end}`;
                              }
                              return uid;
                            })()}
                          </p>
                        </div>
                      </div>

                      {/* 到期日期 - 根据车队类型显示不同字段 */}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-400 flex items-center justify-center shadow-sm">
                          <Calendar className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Expiry Date</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {(() => {
                              let expiryDate = null;
                              
                              // 根据车队类型获取对应的过期时间字段
                              switch(selectedFleet.type) {
                                case 'gpt-backup':
                                  expiryDate = userInfo.expiration_date;
                                  break;
                                case 'claude':
                                  expiryDate = userInfo.expiration_date_ye_claude;
                                  break;
                                case 'gemini':
                                  expiryDate = userInfo.expiration_date_ye_gemini;
                                  break;
                                case 'grok':
                                  expiryDate = userInfo.expiration_date_ye_grok;
                                  break;
                                case 'sora':
                                  expiryDate = userInfo.expiration_date_ye_sora;
                                  break;
                                default:
                                  expiryDate = userInfo.expiration_date;
                              }
                              
                              if (expiryDate) {
                                return new Date(expiryDate).toLocaleDateString('zh-CN', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit'
                                }).replace(/\//g, '-');
                              }
                              return '未开通';
                            })()}
                          </p>
                        </div>
                      </div>

                      {/* 剩余天数 - 根据实际过期时间计算 */}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-sm">
                          <Clock className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Days Remaining</p>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600">
                              {(() => {
                                let expiryDate = null;
                                
                                // 根据车队类型获取对应的过期时间字段
                                switch(selectedFleet.type) {
                                  case 'gpt-backup':
                                    expiryDate = userInfo.expiration_date;
                                    break;
                                  case 'claude':
                                    expiryDate = userInfo.expiration_date_ye_claude;
                                    break;
                                  case 'gemini':
                                    expiryDate = userInfo.expiration_date_ye_gemini;
                                    break;
                                  case 'grok':
                                    expiryDate = userInfo.expiration_date_ye_grok;
                                    break;
                                  case 'sora':
                                    expiryDate = userInfo.expiration_date_ye_sora;
                                    break;
                                  default:
                                    expiryDate = userInfo.expiration_date;
                                }
                                
                                if (expiryDate) {
                                  const expiry = new Date(expiryDate);
                                  const today = new Date();
                                  const diffTime = expiry - today;
                                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                  
                                  if (diffDays > 0) {
                                    return `${diffDays} days`;
                                  } else if (diffDays === 0) {
                                    return '今天到期';
                                  } else {
                                    return '已过期';
                                  }
                                }
                                return '未开通';
                              })()}
                            </p>
                            {/* 迷你进度条 */}
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full transition-all duration-500 relative"
                                style={{
                                  width: (() => {
                                    let expiryDate = null;
                                    
                                    // 根据车队类型获取对应的过期时间字段
                                    switch(selectedFleet.type) {
                                      case 'gpt-backup':
                                        expiryDate = userInfo.expiration_date;
                                        break;
                                      case 'claude':
                                        expiryDate = userInfo.expiration_date_ye_claude;
                                        break;
                                      case 'gemini':
                                        expiryDate = userInfo.expiration_date_ye_gemini;
                                        break;
                                      case 'grok':
                                        expiryDate = userInfo.expiration_date_ye_grok;
                                        break;
                                      case 'sora':
                                        expiryDate = userInfo.expiration_date_ye_sora;
                                        break;
                                      default:
                                        expiryDate = userInfo.expiration_date;
                                    }
                                    
                                    if (expiryDate) {
                                      const expiry = new Date(expiryDate);
                                      const today = new Date();
                                      const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
                                      
                                      if (diffDays > 90) return '100%';
                                      if (diffDays > 0) return `${(diffDays / 90) * 100}%`;
                                      return '0%';
                                    }
                                    return '0%';
                                  })()
                                }}
                              >
                                <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 右侧：操作按钮组 */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                      {/* 充值按钮 */}
                      <button
                        onClick={() => navigate('/purchase')}
                        className="relative group flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 sm:px-6 py-3 sm:py-3 rounded-xl sm:rounded-2xl font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
                      >
                        {/* 动画背景 */}
                        <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        
                        {/* 闪光效果 */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                        
                        {/* 按钮内容 */}
                        <div className="relative flex items-center gap-1.5 sm:gap-2">
                          <Crown className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="text-xs sm:text-sm">Recharge & Upgrade</span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 使用说明栏 - 在账户状态栏和节点列表之间 */}
            {isLoggedIn && userInfo && selectedFleet && (
              <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border border-blue-200 rounded-2xl mb-6 shadow-sm">
                <div className="px-4 sm:px-6 py-4">
                  <div className="flex items-start gap-3">
                    {/* 提示图标 */}
                    <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>

                    {/* 说明内容 */}
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-gray-900 mb-2">💡 重要提示</h4>

                      {/* 通用提示 */}
                      <p className="text-sm text-gray-700 mb-3">
                        <span className="font-semibold text-blue-600">进入新节点后，请开启新对话才能使用该账号！</span>
                        每个节点卡片对应一个独立的AI服务账号。
                      </p>

                      {/* 车队特定提示 - 从配置文件读取 */}
                      {(() => {
                        const fleetConfig = fleetCardsConfig.fleetCards[selectedFleet.type];
                        if (fleetConfig && fleetConfig.importantTips && fleetConfig.importantTips.length > 0) {
                          return (
                            <div className="mt-3 pt-3 border-t border-blue-200">
                              <p className="text-xs font-semibold text-gray-600 mb-2">📌 {selectedFleet.name} 专属提示：</p>
                              <ul className="space-y-1.5">
                                {fleetConfig.importantTips.map((tip, index) => (
                                  <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                                    <span className="text-orange-500 mt-0.5">•</span>
                                    <span>{tip}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 选中车队的节点列表 */}
            {selectedFleet && (
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 relative">
                {/* Loading遮罩 */}
                {loadingNodes && (
                  <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-2xl z-10 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="relative">
                        <div className="w-16 h-16 border-4 border-orange-200 rounded-full"></div>
                        <div className="w-16 h-16 border-4 border-orange-500 rounded-full border-t-transparent animate-spin absolute inset-0"></div>
                      </div>
                      <p className="text-gray-600 font-medium">{t('aiFleet.loadingNodes')}</p>
                      <p className="text-xs text-gray-500">{t('aiFleet.retryMessage')}</p>
                    </div>
                  </div>
                )}
                
                {/* Tab切换栏 - 所有元素在同一行 */}
                <div className="mb-6">
                  <div className="flex items-center justify-between">
                    {/* 左侧：车队图标 + 自助选号标题 + 筛选标签 */}
                    <div className="flex items-center gap-4">
                      {/* 车队图标 */}
                      <div className="w-10 h-10 rounded-lg overflow-hidden shadow-sm">
                        <img 
                          src={getAIStyle(selectedFleet.type).image}
                          alt={selectedFleet.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div 
                          className={`w-full h-full bg-gradient-to-br ${getAIStyle(selectedFleet.type).gradient} rounded flex items-center justify-center`}
                          style={{display: 'none'}}
                        >
                          {React.createElement(getAIStyle(selectedFleet.type).icon, { className: "w-5 h-5 text-white" })}
                        </div>
                      </div>
                      
                      {/* 自助选号标题 */}
                      <h4 className="text-lg font-bold text-gray-900">{t('aiFleet.selfSelectNumber')}</h4>
                      
                      {/* 筛选标签 */}
                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          // 获取当前车队的筛选配置
                          const fleetType = selectedFleet.type;
                          const enabledCategories = defaultFilterConfig.fleetCategories[fleetType] || [];
                          const fleetTags = fleetFilterTags[fleetType];
                          
                          if (!fleetTags || enabledCategories.length === 0) return null;
                          
                          return enabledCategories.flatMap(category => {
                            if (!fleetTags[category]) return [];
                            
                            return fleetTags[category].map(tag => {
                              const isActive = activeFilters[category] === tag.id;
                              const colorStyles = getTagColorStyles(tag.color);
                              
                              return (
                                <button
                                  key={`${category}-${tag.id}`}
                                  onClick={() => handleFilterClick(category, tag.id)}
                                  className={`
                                    flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium
                                    transition-all duration-200 border
                                    ${isActive 
                                      ? `${colorStyles.activeBg} ${colorStyles.activeText}` 
                                      : `${colorStyles.bg} ${colorStyles.text} hover:shadow-sm`
                                    }
                                  `}
                                >
                                  <span>{t(tag.nameKey, tag.name)}</span>
                                  {/* 显示匹配的节点数量 */}
                                  {selectedFleet && nodeData[selectedFleet.id] && (
                                    <span className={`
                                      px-1.5 py-0.5 rounded-full text-xs font-bold
                                      ${isActive ? 'bg-white/20' : 'bg-gray-200 text-gray-600'}
                                    `}>
                                      {nodeData[selectedFleet.id].filter(tag.filter).length}
                                    </span>
                                  )}
                                </button>
                              );
                            });
                          });
                        })()}
                      </div>
                    </div>
                    
                    {/* 右侧：状态指示灯 */}
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        {t('aiFleet.busyLevel.idle')}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                        {t('aiFleet.busyLevel.moderate')}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        {t('aiFleet.busyLevel.busy')}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* 节点列表 */}
                <NodeGrid 
                  nodes={filteredNodes} 
                  fleetType={selectedFleet.type}
                />
              </div>
            )}
          </>
        ) : (
          <EmptyState />
        )}

      </div>
      
      {/* 过期提示弹窗 */}
      {expiredModal.visible && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 animate-fade-in-scale border border-gray-100 relative overflow-hidden">
            {/* 装饰性背景 */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-full blur-3xl opacity-50 -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-red-100 to-orange-100 rounded-full blur-2xl opacity-40 translate-y-24 -translate-x-24"></div>
            
            {/* 关闭按钮 */}
            <button
              onClick={() => setExpiredModal({ visible: false, fleetName: '', expiryDate: null })}
              className="absolute top-6 right-6 p-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all duration-300 group z-10"
            >
              <X className="w-5 h-5 text-gray-600 group-hover:text-gray-800 group-hover:rotate-90 transition-all duration-300" />
            </button>
            
            {/* 小猴子Logo - 替代警告图标 */}
            <div className="relative z-10 w-24 h-24 rounded-2xl overflow-hidden shadow-2xl mx-auto mb-6 bg-gradient-to-br from-orange-400 to-orange-600 p-1">
              <div className="w-full h-full rounded-xl overflow-hidden bg-white">
                <img 
                  src="/images/logo.jpg" 
                  alt="AI Monkey Logo" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div 
                  className="w-full h-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center"
                  style={{display: 'none'}}
                >
                  <Bot className="w-12 h-12 text-white" />
                </div>
              </div>
            </div>
            
            {/* 标题 - 使用渐变文字 */}
            <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-600 text-center mb-2 relative z-10">
              {t('aiFleet.expired.title')}
            </h3>
            
            {/* 副标题 */}
            <p className="text-center text-gray-500 text-sm mb-6 relative z-10">
              {t('aiFleet.expired.subtitle')}
            </p>
            
            {/* 账户信息卡片 - 优化设计 */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-4 mb-6 border border-gray-200 relative z-10">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center shadow-lg">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">{t('aiFleet.expired.currentAccount')}</p>
                  <p className="text-sm font-bold text-gray-900 mt-0.5" title={userInfo?.uid || userInfo?.id || userInfo?.email || '用户'}>
                    {(() => {
                      const uid = userInfo?.uid || userInfo?.id || userInfo?.email || '用户';
                      if (uid.length > 20) {
                        const start = uid.substring(0, 8);
                        const end = uid.substring(uid.length - 8);
                        return `${start}...${end}`;
                      }
                      return uid;
                    })()}
                  </p>
                </div>
              </div>
            </div>
            
            {/* 服务信息 - 卡片式设计 */}
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-5 mb-8 border border-red-200 relative z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-600">{t('aiFleet.expired.expiredService')}</span>
                <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full">{t('aiFleet.expired.expiredLabel')}</span>
              </div>
              <p className="text-lg font-bold text-gray-900 mb-2">
                {expiredModal.fleetName}
              </p>
              <div className="flex items-center gap-2 text-sm text-red-600">
                <Calendar className="w-4 h-4" />
                <span>{t('aiFleet.expired.expiryTime')}{expiredModal.expiryDate && new Date(expiredModal.expiryDate).toLocaleDateString('zh-CN', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })}</span>
              </div>
            </div>
            
            {/* 续费按钮 - 单独一个醒目的按钮 */}
            <button
              onClick={() => {
                setExpiredModal({ visible: false, fleetName: '', expiryDate: null });
                navigate('/shop?type=web');
              }}
              className="w-full relative group overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 flex items-center justify-center gap-3">
                <Crown className="w-6 h-6" />
                <span className="text-lg font-bold">{t('aiFleet.expired.renewNow')}</span>
                <div className="absolute right-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300">
                  →
                </div>
              </div>
            </button>
            
            {/* 底部提示文字 */}
            <p className="text-center text-xs text-gray-400 mt-4 relative z-10">
              {t('aiFleet.expired.renewToRestore')}
            </p>
          </div>
        </div>
      )}

      {/* 手动跳转弹窗 */}
      {manualRedirectModal.visible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 transform relative">
            {/* 关闭按钮 */}
            <button
              onClick={() => setManualRedirectModal({ visible: false, bridgeUrl: '', fleetName: '' })}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-200 group"
            >
              <X className="w-4 h-4 text-gray-500 group-hover:text-gray-700" />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bot className="w-8 h-8 text-blue-500" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                🚀 {t('aiFleet.manualRedirect.ready')}
              </h3>
              
              <p className="text-gray-600 text-sm mb-4">
                {t('aiFleet.manualRedirect.description')} <strong>{manualRedirectModal.fleetName}</strong>
              </p>
              
              <p className="text-gray-500 text-xs mb-6">
                {t('aiFleet.manualRedirect.instruction')}
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => window.location.href = manualRedirectModal.bridgeUrl}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-4 rounded-xl font-bold hover:from-blue-600 hover:to-purple-600 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg transform hover:scale-105"
                >
                  <Bot className="w-5 h-5" />
                  {t('aiFleet.manualRedirect.openManually')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
} 